import {Component, OnInit, OnDestroy, ElementRef, ViewChild} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import {CDSNode, Org} from '../services/capd.model';
import { OrgService } from '../services/org.service';
import { APIURL } from "../services/url.config";
import {AlertService} from "../services/alert.service";
import { SecureTokenService } from '../services/secure-token.service';

@Component({
    selector: 'tenant-mappings',
    templateUrl: './mappings-tenant-org.component.html',
    providers: [OrgService, Location]
})

/**
 * Component for the org/company settings page.
 */
export class TenantMappingsComponent implements OnInit, OnDestroy {
    // Current mapping for org
    thisMapping: any = null;

    // TODO: Generate list of application options, default to engage, but expand later on
    application: any = 'engage';
    applicationList: ['engage'];

    // // Body/URL variables for API requests
    tenant: any;
    oid: any;
    url: any;
    currAction: any = 'Create';
    username: String = sessionStorage.getItem('username');

    @ViewChild('confirmDeleteModal') confirmDeleteModal: ElementRef;

    // Form initialization to Create & Update
    createForm = new UntypedFormGroup({
        tenant: new UntypedFormControl(),
    });

    updateForm = new UntypedFormGroup({
        tenant: new UntypedFormControl(),
    });

    // Organization settings
    orgName: string = '';
    org: Org = {
        _id: '',
        settings: { }
    };
    root: string;
    expandedRoot: string;
    scope: string;
    private sub: any;

    constructor(private _service: OrgService,
                private _router: Router,
                private _route: ActivatedRoute,
                private http: HttpClient,
                private _el: ElementRef,
                private alertService: AlertService) {
    }

    /**
     * Get the current org settings object and, if anything is unset, add default values.
     *
     * @async
     * @func ngOnInit
     */
    ngOnInit() {
        this.scope = sessionStorage.getItem('scope');
        this.sub = this._route.params.subscribe(params => {
            this.root = params['id'];
            this.expandedRoot = this.root.replace('~', '2.16.840.1.113883.3.21');

            let firstTest = this.root.indexOf('+');
            if (firstTest > 0) {
                this._router.navigate(['users']);
            }

            this._service.getOrg(this.root).then((pak: CDSNode) => this.orgName = pak.description);
            this._service.getOrgSettings(this.root).then((org: Org) => {
                this.org = org;
            })
                .catch(err => {
                    if (this.org === null) {
                        // setting does not exist yet, so we're creating a new one.
                        this.org = <Org>{ settings: { } }
                    }
                    this.org._id = this.root;
                })
        });
        this.loadMapping();
    }

    /**
     * Disposes of the subscriber object.
     *
     * @async
     * @func ngOnDestroy
     */
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // Load all currently existing tenant mappings across all systems
    public async loadMapping() {
        // Implemented onInit
        let myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", "Bearer " + SecureTokenService.getAccessToken());

        let requestOptions: RequestInit = {
            method: 'GET',
            headers: myHeaders,
            redirect: 'follow'
        };

        this.url = APIURL + 'api/uaa/tenant_mapping?oid=' + this.expandedRoot;
        // Retrieve mappings currently set
        let mappingVal = fetch(this.url, requestOptions)
            .then(response => response.json())
            .then(result => {
                if(result.oid !== null && result.oid !== '' && result.oid !== undefined) {
                    this.thisMapping = result;
                }
                this.setAction();
                return result;
            })
            .then(next => {
                this.clearForms();
            })
            .catch(error => {
                console.log('Mapping Tenant Retrieval Failed.');
                console.log('error', error);
            });
        await mappingVal;
    }

    // Submit form values to Create, Update, or Delete mapping
    onSubmit(formValues) {
        // set up the api call
        const headers = new HttpHeaders()
            .set('Authorization', 'Bearer ' + SecureTokenService.getAccessToken())
            .set('cache-control', 'no-cache, private')
            .set('Content-Type', 'application/json');

        const body = {
            oid: this.expandedRoot,
            tenant: formValues.tenant,
            application: this.application
        };

        this.url = APIURL + 'api/uaa/tenant_mapping';

        // Check action, ensure a parameter was given, and ensure the parameter is not empty
        if (this.currAction === 'Create' && formValues.tenant !== null && formValues.tenant.trim() !== '') {
            console.log('Creating mapping for tenant: ', formValues.tenant);
            this.createMapping(body, headers);
        } else if (this.currAction === 'Update' && formValues.tenant !== null && formValues.tenant.trim() !== '') {
            console.log('Updating mapping for tenant: ', formValues.tenant);
            this.updateMapping(body, headers);
        } else {
            this.alertService.showAlert('Invalid Parameters Provided.\nPlease ensure the Tenant given meets the following:\n ' +
                '- Not empty\n - Does not contain only spaces\n - Under 100 characters');
            return;
        }
    }

    // Create mapping API request
    private createMapping(body, headers) {
        return this.http
            .post(this.url, body, {headers: headers})
            .subscribe(
                out => {
                    console.log('CREATE successful');
                    this.currAction = 'Update';
                    this.loadMapping();
                }, response => {
                    console.log('CREATE call in error', response);
                    this.alertService.showAlert("Failed to create new mapping.");
                }, () => {
                    console.log('CREATE completed.');
                }
            );
    }

    // Update mapping API request
    private updateMapping(body, headers) {
        return this.http
            .put(this.url, body, { headers: headers})
            .subscribe(
                val => {
                    console.log('UPDATE successful');
                    this.loadMapping();
                }, response => {
                    console.log('UPDATE call in error', response);
                    this.alertService.showAlert("Failed to update mapping.");
                }, () => {
                    console.log('UPDATE completed.');
                }
            );
    }

    // Delete mapping API request
    deleteMapping() {
        const headers = new HttpHeaders()
            .set('Authorization', 'Bearer ' + SecureTokenService.getAccessToken())
            .set('cache-control', 'no-cache, private')
            .set('Content-Type', 'application/json');

        // tslint:disable-next-line:max-line-length
        this.url = APIURL + 'api/uaa/tenant_mapping?oid=' + this.thisMapping.oid;
        this.confirmDeleteModal.nativeElement.classList.toggle("show");

        return this.http
            .delete(this.url, { headers: headers })
            .subscribe(
                val => {
                    console.log('DELETE successful');
                    this.thisMapping = null;
                    this.tenant = null;
                    this.currAction = 'Create';
                    this.loadMapping();
                }, response => {
                    console.log('DELETE call in error', response);
                    this.alertService.showAlert("Failed to Delete Mapping.");
                }, () => {
                    console.log('DELETE completed.');
                }
            );
    }

    // Alert user if input value reaches max of 100 characters
    checkInputLength(elementId) {
        let input = (<HTMLInputElement>document.getElementById(elementId));

        if(input.value !== null && input.value !== undefined) {
            if(input.value.length >= 100) {
                this.alertService.showAlert('Input value reached max length of 100 characters.');
            }
        }
    }

    // Update action to display proper form & execute proper API request
    setAction() {
        if(this.thisMapping == null) {
            this.currAction = 'Create';
        } else {
            this.currAction = 'Update';
        }
    }

    // Clear the Create and Update forms so no data is mistakenly re-submitted
    clearForms() {
        this.createForm.reset();
        this.updateForm.reset();
    }

    toggleDeleteModal() {
        this.confirmDeleteModal.nativeElement.classList.toggle("show");
    }

    enableDisableMapping(elementId) {
        let input = (<HTMLInputElement>document.getElementById(elementId));
        if(input.value !== null && input.value !== undefined && input.value.length > 0)
            return false;
        else
            return true;
    }
}
